import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Dialog,
  DialogTitle,
  Grid,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Stack,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DataContext } from "../context/DataContext";

// Helper function to safely get numeric value
const getSafeNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

const WeightAdjustmentDialog = ({
  open,
  onClose,
  item,
  originalWeight,
  newWeight,
  weightDifference,
  isWeightIncreased,
  onAdjustmentComplete,
  onSkip,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { stock, updateStock, addStock, bulkUpdateSilverStock } =
    useContext(DataContext);

  // Safely get numeric values
  const safeWeightDifference = getSafeNumber(weightDifference, 0);
  const safeOriginalWeight = getSafeNumber(originalWeight, 0);
  const safeNewWeight = getSafeNumber(newWeight, 0);
  const absoluteWeight = Math.abs(safeWeightDifference);

  const [selectedOption, setSelectedOption] = useState("");
  const [selectedMetal, setSelectedMetal] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchSerial, setSearchSerial] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchMetal, setSearchMetal] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // const [grossStockOptions, setGrossStockOptions] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [isGeneratingSerial, setIsGeneratingSerial] = useState(false);

  // Get parent item details
  const parentItem = item?.itemObject || {};

  const [newItemDetails, setNewItemDetails] = useState({
    name: item?.name || parentItem.name || "",
    metal: item?.metal || parentItem.metal || "",
    weight: absoluteWeight.toFixed(3),
    serialNo: "",
    // Pre-fill with parent item details
    makingCharges: parentItem.makingCharges || item?.makingCharges || 0,
    metalRate: parentItem.metalRate || item?.metalRate || 0,
    wastage: parentItem.wastage || item?.wastage || 0,
    melting: parentItem.melting || 0,
    grossWeight: parentItem.grossWeight || 0,
    lessWeight: parentItem.lessWeight || 0,
    labour: parentItem.labour || 0,
    silverRate: parentItem.silverRate || 0,
    goldCuttingRate: parentItem.goldCuttingRate || 0,
    description: parentItem.description || "",
    itemDetail: parentItem.itemDetail || "",
    supplierName: parentItem.supplierName || "",
    supplierShortcut: parentItem.supplierShortcut || "",
    huid: parentItem.huid || item?.huid || "",
  });

  // Get company details from context
  const { companyDetails } = useContext(DataContext);

  // Reset newItemDetails when dialog opens with new item
  useEffect(() => {
    if (open && !isWeightIncreased && item) {
      const parent = item?.itemObject || {};
      const safeDiff = getSafeNumber(weightDifference, 0);
      setNewItemDetails({
        name: item?.name || parent.name || "",
        metal: item?.metal || parent.metal || "",
        weight: Math.abs(safeDiff).toFixed(3),
        serialNo: "",
        makingCharges: parent.makingCharges || item?.makingCharges || 0,
        metalRate: parent.metalRate || item?.metalRate || 0,
        wastage: parent.wastage || item?.wastage || 0,
        melting: parent.melting || 0,
        grossWeight: parent.grossWeight || 0,
        lessWeight: parent.lessWeight || 0,
        labour: parent.labour || 0,
        silverRate: parent.silverRate || 0,
        goldCuttingRate: parent.goldCuttingRate || 0,
        description: parent.description || "",
        itemDetail: parent.itemDetail || "",
        supplierName: parent.supplierName || "",
        supplierShortcut: parent.supplierShortcut || "",
        huid: parent.huid || item?.huid || "",
      });
    }
  }, [open, item, weightDifference, isWeightIncreased]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      // Load item categories
      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  // Load gross stock options
  // useEffect(() => {
  //   if (open && grossStock) {
  //     setGrossStockOptions(grossStock);
  //   }
  // }, [open, grossStock]);

  // Function to find first missing number in a sorted array
  const findFirstMissingNumber = useCallback((arr) => {
    if (arr.length === 0) return 1;
    const numberSet = new Set(arr);
    let i = 1;
    while (numberSet.has(i)) {
      i++;
    }
    return i;
  }, []);

  // Get next serial number function
  const getNextSerialNumber = useCallback(
    (itemName, metal) => {
      if (!itemName || !metal) return 1;

      const allItemsForType =
        stock?.filter(
          (item) => item.name === itemName && item.metal === metal,
        ) || [];

      if (allItemsForType.length === 0) {
        return 1;
      }

      const unsoldItems = allItemsForType.filter(
        (item) => item.sold === "No" || item.sold === false,
      );
      const unsoldSerialNumbers = unsoldItems
        .map((item) => parseInt(item.serialNo) || 0)
        .filter((num) => !isNaN(num) && num > 0)
        .sort((a, b) => a - b);

      const firstMissing = findFirstMissingNumber(unsoldSerialNumbers);
      return firstMissing;
    },
    [stock, findFirstMissingNumber],
  );

  // Auto-generate serial number when item name or metal changes
  useEffect(() => {
    const updateSerialNumber = async () => {
      if (
        selectedOption === "add_new_item" &&
        newItemDetails.name &&
        newItemDetails.metal
      ) {
        setIsGeneratingSerial(true);
        try {
          const nextSerial = getNextSerialNumber(
            newItemDetails.name,
            newItemDetails.metal,
          );
          setNewItemDetails((prev) => ({
            ...prev,
            serialNo: nextSerial.toString(),
          }));
        } catch (error) {
          console.error("Error generating serial number:", error);
        } finally {
          setIsGeneratingSerial(false);
        }
      }
    };

    updateSerialNumber();
  }, [
    newItemDetails.name,
    newItemDetails.metal,
    selectedOption,
    getNextSerialNumber,
  ]);

  // Search for items by serial, name, or metal
  const handleSearchItem = useMemo(() => {
    const search = () => {
      let results =
        stock?.filter(
          (stockItem) =>
            stockItem.sold !== "Yes" && stockItem.id !== item?.itemObject?.id,
        ) || [];

      if (searchSerial) {
        results = results?.filter((i) =>
          i.serialNo?.toString().includes(searchSerial),
        );
      }
      if (searchName) {
        results = results?.filter((i) =>
          i.name?.toLowerCase().includes(searchName.toLowerCase()),
        );
      }
      if (searchMetal) {
        results = results?.filter((i) =>
          i.metal?.toLowerCase().includes(searchMetal.toLowerCase()),
        );
      }

      return results;
    };
    return search();
  }, [stock, searchSerial, searchName, searchMetal, item]);

  useEffect(() => {
    setSearchResults(handleSearchItem);
  }, [handleSearchItem]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      switch (selectedOption) {
        case "ignore":
          onAdjustmentComplete({ action: "ignore" });
          onClose();
          break;

        case "reduce_from_gross_stock":
        case "increase_from_gross_stock":
          if (!selectedMetal) {
            alert("Please select a metal");
            setIsLoading(false);
            return;
          }

          // Update gross stock
          const grossStockOperation = isWeightIncreased ? "subtract" : "add";
          const grossStockResult = await bulkUpdateSilverStock([
            {
              silverType: selectedMetal === "Gold" ? "gold" : selectedMetal?.toLowerCase(),
              netWeight: absoluteWeight.toFixed(3),
              operation: grossStockOperation,
              description: `Weight adjustment for ${item?.name} (${item?.itemObject?.serialNo}) - ${isWeightIncreased ? "Increased" : "Reduced"} by ${absoluteWeight.toFixed(3)}g`,
            },
          ]);

          if (!grossStockResult?.success) {
            throw new Error(grossStockResult?.message || "Failed to update gross stock");
          }

          // Update the original item's weight using the correct item ID
          const itemId = item?.itemObject?.id || item?.id;
          if (itemId) {
            // Create updated item data with new weight
            const updatedItemData = {
              ...(item?.itemObject || item),
              weight: safeNewWeight,
            };

            // Call updateStock with the item ID and updated data
            const updateResult = await updateStock(itemId, updatedItemData);

            if (!updateResult?.success) {
              throw new Error(updateResult?.message || "Failed to update item weight");
            }
          }

          onAdjustmentComplete({
            action: "gross_stock",
            metal: selectedMetal,
            weight: absoluteWeight.toFixed(3),
            operation: grossStockOperation,
          });
          onClose();
          break;

        case "reduce_from_another_item":
        case "increase_in_another_item":
          if (!selectedItem) {
            alert("Please select an item");
            setIsLoading(false);
            return;
          }

          const updatedWeight = isWeightIncreased
            ? getSafeNumber(selectedItem.weight) - absoluteWeight
            : getSafeNumber(selectedItem.weight) + absoluteWeight;

          if (updatedWeight <= 0) {
            alert("Item weight cannot be zero or negative");
            setIsLoading(false);
            return;
          }

          // Update the selected item's weight
          const selectedItemId = selectedItem.id;
          const selectedItemUpdated = {
            ...selectedItem,
            weight: updatedWeight,
          };
          const updateSelectedResult = await updateStock(selectedItemId, selectedItemUpdated);

          if (!updateSelectedResult?.success) {
            throw new Error(updateSelectedResult?.message || "Failed to update selected item");
          }

          // Update the original item's weight
          const originalItemId = item?.itemObject?.id || item?.id;
          if (originalItemId) {
            const originalItemUpdated = {
              ...(item?.itemObject || item),
              weight: safeNewWeight,
            };
            const updateOriginalResult = await updateStock(originalItemId, originalItemUpdated);

            if (!updateOriginalResult?.success) {
              throw new Error(updateOriginalResult?.message || "Failed to update original item");
            }
          }

          onAdjustmentComplete({
            action: "another_item",
            item: selectedItem,
            weight: absoluteWeight.toFixed(3),
            newWeight: updatedWeight,
          });
          onClose();
          break;

        case "add_new_item":
          if (!newItemDetails.name || !newItemDetails.metal) {
            alert("Please enter item name and metal");
            setIsLoading(false);
            return;
          }

          const nextSerial = newItemDetails.serialNo ||
            (await getNextSerialNumber(newItemDetails.name, newItemDetails.metal));

          // Get parent item details from the itemObject
          const parentItemObj = item?.itemObject || {};

          // Create new item with all parent details, only updating weight and serial
          const newItem = {
            ...parentItemObj,
            name: newItemDetails.name,
            metal: newItemDetails.metal,
            weight: absoluteWeight.toFixed(3),
            serialNo: nextSerial.toString(),
            sold: "No",
            qr: `JWL${Date.now()}`,
            makingCharges: newItemDetails.makingCharges || parentItemObj.makingCharges || 0,
            metalRate: newItemDetails.metalRate || parentItemObj.metalRate || 0,
            wastage: newItemDetails.wastage || parentItemObj.wastage || 0,
            melting: newItemDetails.melting || parentItemObj.melting || 0,
            grossWeight: absoluteWeight.toFixed(3),
            lessWeight: 0,
            price: 0,
            labour: newItemDetails.labour || parentItemObj.labour || 0,
            silverRate: newItemDetails.silverRate || parentItemObj.silverRate || 0,
            goldCuttingRate: newItemDetails.goldCuttingRate || parentItemObj.goldCuttingRate || 0,
            description: newItemDetails.description || parentItemObj.description || "",
            itemDetail: newItemDetails.itemDetail || parentItemObj.itemDetail || "",
            supplierName: newItemDetails.supplierName || parentItemObj.supplierName || "",
            supplierShortcut: newItemDetails.supplierShortcut || parentItemObj.supplierShortcut || "",
            huid: newItemDetails.huid || parentItemObj.huid || "",
            id: undefined,
            _id: undefined,
            createdAt: undefined,
            updatedAt: undefined,
          };

          // Add the new item to stock
          const addResult = await addStock(newItem);

          if (!addResult?.success) {
            throw new Error(addResult?.message || "Failed to add new item");
          }

          // Update the original item's weight to the new weight (reduced weight)
          const originalItemIdForNew = item?.itemObject?.id || item?.id;
          if (originalItemIdForNew) {
            const originalItemUpdated = {
              ...(item?.itemObject || item),
              weight: safeNewWeight,
            };
            const updateOriginalResult = await updateStock(originalItemIdForNew, originalItemUpdated);

            if (!updateOriginalResult?.success) {
              throw new Error(updateOriginalResult?.message || "Failed to update original item");
            }
          }

          onAdjustmentComplete({
            action: "new_item",
            item: newItem,
            weight: absoluteWeight.toFixed(3),
            serialNo: nextSerial,
          });
          onClose();
          break;

        default:
          break;
      }
    } catch (error) {
      console.error("Error during weight adjustment:", error);
      alert("Failed to process weight adjustment: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to manually refresh serial number
  const handleRefreshSerial = useCallback(() => {
    if (newItemDetails.name && newItemDetails.metal) {
      const nextSerial = getNextSerialNumber(
        newItemDetails.name,
        newItemDetails.metal,
      );
      setNewItemDetails((prev) => ({
        ...prev,
        serialNo: nextSerial.toString(),
      }));
    }
  }, [newItemDetails.name, newItemDetails.metal, getNextSerialNumber]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: "blur(20px)",
          background: isDark
            ? "rgba(30, 41, 59, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Weight Adjustment Required
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              Item: {item?.name} - {item?.metal}
              <br />
              Original Weight: {safeOriginalWeight.toFixed(3)}g → New Weight: {safeNewWeight.toFixed(3)}g
              <br />
              Difference: {safeWeightDifference.toFixed(3)}g (
              {isWeightIncreased ? "Increased" : "Reduced"})
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => {
              onSkip();
              onClose();
            }}
            disabled={isLoading}
          >
            Skip Adjustment
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          The weight of this item has been changed. Please select how you want
          to handle this change in stock.
        </Alert>

        <RadioGroup
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
        >
          {/* Ignore Option */}
          <Paper
            sx={{
              mb: 2,
              p: 2,
              border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              borderRadius: 2,
            }}
          >
            <FormControlLabel
              value="ignore"
              control={<Radio />}
              label="Ignore weight changes"
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", ml: 4, display: "block" }}
            >
              Stock will not be updated. The weight change will be ignored.
            </Typography>
          </Paper>

          {/* Gross Stock Options */}
          <Paper
            sx={{
              mb: 2,
              p: 2,
              border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              borderRadius: 2,
            }}
          >
            <FormControlLabel
              value={
                isWeightIncreased
                  ? "reduce_from_gross_stock"
                  : "increase_from_gross_stock"
              }
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">
                    {isWeightIncreased
                      ? "Reduce extra weight from Gross Stock"
                      : "Increase reduced weight in Gross Stock"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {isWeightIncreased
                      ? `Reduce ${absoluteWeight.toFixed(3)}g from gross stock of selected metal`
                      : `Add ${absoluteWeight.toFixed(3)}g to gross stock of selected metal`}
                  </Typography>
                </Box>
              }
            />

            {selectedOption ===
              (isWeightIncreased
                ? "reduce_from_gross_stock"
                : "increase_from_gross_stock") && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 2, ml: 4, width: "calc(100% - 32px)" }}
                >
                  <InputLabel>Select Metal</InputLabel>
                  <Select
                    value={selectedMetal}
                    onChange={(e) => setSelectedMetal(e.target.value)}
                    label="Select Metal"
                  >
                    {metalTypes?.map((metal) => (
                      <MenuItem key={metal.id} value={metal.name}>
                        {metal.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
          </Paper>

          {/* Another Item Options with Search */}
          <Paper
            sx={{
              mb: 2,
              p: 2,
              border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              borderRadius: 2,
            }}
          >
            <FormControlLabel
              value={
                isWeightIncreased
                  ? "reduce_from_another_item"
                  : "increase_in_another_item"
              }
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">
                    {isWeightIncreased
                      ? "Reduce from another item"
                      : "Increase in another item"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {isWeightIncreased
                      ? `Select an item to reduce ${absoluteWeight.toFixed(3)}g from its weight`
                      : `Select an item to add ${absoluteWeight.toFixed(3)}g to its weight`}
                  </Typography>
                </Box>
              }
            />

            {selectedOption ===
              (isWeightIncreased
                ? "reduce_from_another_item"
                : "increase_in_another_item") && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Search by Serial No."
                      value={searchSerial}
                      onChange={(e) => setSearchSerial(e.target.value)}
                      sx={{ flex: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      size="small"
                      placeholder="Search by Item Name"
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      placeholder="Search by Metal"
                      value={searchMetal}
                      onChange={(e) => setSearchMetal(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  </Stack>

                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Select</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>Metal</TableCell>
                          <TableCell>Serial No.</TableCell>
                          <TableCell>Current Weight</TableCell>
                          <TableCell>After Adjustment</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchResults.map((stockItem) => {
                          const newWeightAfter = isWeightIncreased
                            ? getSafeNumber(stockItem.weight) - absoluteWeight
                            : getSafeNumber(stockItem.weight) + absoluteWeight;
                          const isValid = newWeightAfter > 0;

                          return (
                            <TableRow
                              key={stockItem.id}
                              selected={selectedItem?.id === stockItem.id}
                              sx={{
                                cursor: "pointer",
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.05,
                                  ),
                                },
                                bgcolor:
                                  selectedItem?.id === stockItem.id
                                    ? alpha(theme.palette.primary.main, 0.1)
                                    : "transparent",
                              }}
                              onClick={() =>
                                isValid && setSelectedItem(stockItem)
                              }
                            >
                              <TableCell>
                                <Radio
                                  checked={selectedItem?.id === stockItem.id}
                                  onChange={() =>
                                    isValid && setSelectedItem(stockItem)
                                  }
                                  disabled={!isValid}
                                />
                              </TableCell>
                              <TableCell>{stockItem.name}</TableCell>
                              <TableCell>{stockItem.metal}</TableCell>
                              <TableCell>{stockItem.serialNo}</TableCell>
                              <TableCell>
                                {getSafeNumber(stockItem.weight).toFixed(3)} g
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    color: !isValid
                                      ? "error.main"
                                      : newWeightAfter > getSafeNumber(stockItem.weight)
                                        ? "success.main"
                                        : "warning.main",
                                    fontWeight: 600,
                                  }}
                                >
                                  {newWeightAfter.toFixed(3)} g
                                  {!isValid && " (Invalid)"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {selectedItem && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Selected: {selectedItem.name} - {selectedItem.metal} (SNo:{" "}
                      {selectedItem.serialNo})
                      <br />
                      {isWeightIncreased
                        ? "New weight will be reduced to"
                        : "New weight will be increased to"}
                      :{" "}
                      {(isWeightIncreased
                        ? getSafeNumber(selectedItem.weight) - absoluteWeight
                        : getSafeNumber(selectedItem.weight) + absoluteWeight
                      ).toFixed(3)}
                      g
                    </Alert>
                  )}
                </Box>
              )}
          </Paper>

          {/* Add New Item Option (only for weight reduction) */}
          {!isWeightIncreased && (
            <Paper
              sx={{
                mb: 2,
                p: 2,
                border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                borderRadius: 2,
              }}
            >
              <FormControlLabel
                value="add_new_item"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      Add new item with remaining weight
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Create a new item with the reduced weight
                    </Typography>
                  </Box>
                }
              />

              {selectedOption === "add_new_item" && (
                <Stack spacing={2} sx={{ mt: 2, ml: 4 }}>
                  <Autocomplete
                    freeSolo
                    options={itemCategories}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${option.name} (${option.shortcut})`;
                    }}
                    value={newItemDetails.name}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        setNewItemDetails({
                          ...newItemDetails,
                          name: newValue,
                        });
                      } else if (newValue?.name) {
                        setNewItemDetails({
                          ...newItemDetails,
                          name: newValue.name,
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item Name *"
                        size="small"
                        fullWidth
                        required
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...restProps } = props;
                      return (
                        <li key={key} {...restProps}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Typography variant="body2">
                              {option.name}
                            </Typography>
                            <Chip
                              label={option.shortcut}
                              size="small"
                              sx={{ ml: 1, fontSize: "0.7rem" }}
                            />
                          </Box>
                        </li>
                      );
                    }}
                  />

                  <Autocomplete
                    options={metalTypes}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${option.name} (${option.shortcut})`;
                    }}
                    value={newItemDetails.metal}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        setNewItemDetails({
                          ...newItemDetails,
                          metal: newValue,
                        });
                      } else if (newValue?.name) {
                        setNewItemDetails({
                          ...newItemDetails,
                          metal: newValue.name,
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Metal *"
                        size="small"
                        fullWidth
                        required
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...restProps } = props;
                      return (
                        <li key={key} {...restProps}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Typography variant="body2">
                              {option.name}
                            </Typography>
                            <Chip
                              label={option.shortcut}
                              size="small"
                              sx={{ ml: 1, fontSize: "0.7rem" }}
                            />
                          </Box>
                        </li>
                      );
                    }}
                  />

                  <TextField
                    label="Serial Number"
                    value={newItemDetails.serialNo}
                    onChange={(e) =>
                      setNewItemDetails({
                        ...newItemDetails,
                        serialNo: e.target.value,
                      })
                    }
                    size="small"
                    fullWidth
                    placeholder="Auto-generated based on existing items"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleRefreshSerial}
                            disabled={
                              isGeneratingSerial ||
                              !newItemDetails.name ||
                              !newItemDetails.metal
                            }
                            title="Refresh serial number"
                          >
                            {isGeneratingSerial ? (
                              <CircularProgress size={16} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText={
                      newItemDetails.name && newItemDetails.metal
                        ? `Next available serial number for ${newItemDetails.name} (${newItemDetails.metal})`
                        : "Select item name and metal to auto-generate serial number"
                    }
                  />

                  <TextField
                    label="Weight (g)"
                    value={absoluteWeight.toFixed(3)}
                    size="small"
                    fullWidth
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">g</InputAdornment>
                      ),
                    }}
                  />

                  {/* Display inherited details */}
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Inherited from parent item:
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Metal Rate: ₹{newItemDetails.metalRate}/g
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Making Charges: {newItemDetails.makingCharges}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Wastage: {newItemDetails.wastage}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Melting: {newItemDetails.melting}%
                        </Typography>
                      </Grid>
                      {newItemDetails.supplierName && (
                        <Grid item xs={12}>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            Supplier: {newItemDetails.supplierName} (
                            {newItemDetails.supplierShortcut})
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {newItemDetails.serialNo && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      New item will be created with:
                      <br />• Name: {newItemDetails.name}
                      <br />• Metal: {newItemDetails.metal}
                      <br />• Serial No: {newItemDetails.serialNo}
                      <br />• Weight: {absoluteWeight.toFixed(3)}g
                      <br />• All other details inherited from parent item
                    </Alert>
                  )}
                </Stack>
              )}
            </Paper>
          )}
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedOption || isLoading}
          sx={{
            background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
          }}
        >
          {isLoading ? "Processing..." : "Apply Adjustment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeightAdjustmentDialog;